/**
 * EventTypeIcon — the event-type glyphs, exported from the Figma rather than drawn here.
 *
 * Source: Rocky-Marketing, the marketer home's legend (1:8469, nodes 1:8578 / 1:8581 /
 * 1:8584 / 1:8587). The SVG paths and colours below are Figma's export, unchanged.
 *
 * The design names four categories (1P Flagship, 1P Other, 3P Events, Competitor Events);
 * the service has five types. Mapped as agreed:
 *
 *   ONE_P_GLOBAL                     solid blue dot       (Figma "1P Flagship")
 *   ONE_P_LOCAL                      green ring           (Figma "1P Other")
 *   THREE_P_GLOBAL                   purple square        (Figma "3P Events")
 *   THREE_P_LOCAL                    pink square          (not in the Figma, see below)
 *   COMPETITOR                       orange triangle      (Figma "Competitor Events")
 *
 * The Figma has one "3P Events" glyph, and both 3P types used to share it — so the calendar
 * could not tell them apart. At PM request 3P Local keeps the square but takes its own colour,
 * a pink (#E87FD4) chosen to sit next to the purple without being mistaken for it.
 *
 * Two sizes, as the design draws them: `legend` (12px — legend, table, cards) and `chip`
 * (8px — inside a calendar chip). Decorative: the type is always named in text beside it, so
 * the SVG is aria-hidden.
 */

import type { EventType } from '../../client/EventCalendarClient';

export type EventTypeIconSize = 'legend' | 'chip';

/** Box size in px. The glyphs keep their own proportions inside it (the square is 11/12). */
const BOX: Record<EventTypeIconSize, number> = { legend: 12, chip: 8 };

function Glyph({ type }: { type: EventType }) {
  switch (type) {
    case 'ONE_P_GLOBAL':
      return <circle cx="6" cy="6" r="6" fill="#42B4FF" />;
    case 'ONE_P_LOCAL':
      return <circle cx="6" cy="6" r="4.5" stroke="#2BB534" strokeWidth="3" fill="none" />;
    case 'THREE_P_GLOBAL':
      return <rect x="0.5" y="0.5" width="11" height="11" rx="1" fill="#BF80FF" />;
    case 'THREE_P_LOCAL':
      return <rect x="0.5" y="0.5" width="11" height="11" rx="1" fill="#E87FD4" />;
    case 'COMPETITOR':
      return (
        <path
          transform="translate(0.5 1.1)"
          d="M4.60661 0.515798C5.00367 -0.171933 5.99633 -0.171933 6.39339 0.515798L10.8603 8.25277C11.2574 8.9405 10.7611 9.80016 9.96694 9.80016H1.03306C0.238934 9.80016 -0.257393 8.9405 0.139669 8.25277L4.60661 0.515798Z"
          fill="#FA6F00"
        />
      );
  }
}

export default function EventTypeIcon({
  type,
  size = 'legend',
  className,
}: {
  type: EventType;
  size?: EventTypeIconSize;
  className?: string;
}) {
  const px = BOX[size];
  return (
    <svg
      className={className}
      width={px}
      height={px}
      /* One 12×12 box for every glyph, so they line up on a common baseline and centre. */
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      data-event-type={type}
    >
      <Glyph type={type} />
    </svg>
  );
}
